import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  Identifiable,
  Comment as C,
  DiscussionSubjectTypes,
} from "@planzone/dto";

import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { EventIcon } from "@common/eventIcon";
import { MilestoneIcon } from "@common/milestoneIcon";
import {
  useAuthentication,
  usePostComment,
  useRetrieveDiscussion,
} from "@planzone/view-models";
import { Props } from "./types";
import { ToggleArrow } from "./toggleArrow";
import { Comment } from "./comment";
import { Comment as CommentLoader } from "./loader";
import { TaskIcon } from "./taskIcon";
import { ListIcon } from "./listIcon";
import { ProjectIcon } from "./projectIcon";
import { ChatInput } from "./chatInput";
import styles from "./styles.scss";
import { TrashIcon } from "@common/trashIcon";
import { ContextualMenu } from "@common/contextualMenu";

function sortBySubmissionDate(current: Identifiable<C>, next: Identifiable<C>) {
  return new Date(current.createdDate) < new Date(next.createdDate) ? -1 : 1;
}

function toDiscussionType(entityType: DiscussionSubjectTypes): string {
  return entityType === DiscussionSubjectTypes.EVENT
    ? "event"
    : entityType === DiscussionSubjectTypes.MILESTONE
      ? "milestone"
      : entityType === DiscussionSubjectTypes.LIST
        ? "list"
        : entityType === DiscussionSubjectTypes.PROJECT
          ? "project"
          : "task";
}

const COMMENTS_PAGE_SIZE = 10;
const LOAD_OLDER_COMMENTS_SCROLL_THRESHOLD = 40;

function Discussion({ project, discussion: discussionRow, resources }: Props) {
  const { t } = useTranslation();
  const { account } = useAuthentication();
  const [isOpen, setIsOpen] = React.useState(true);
  const [replyTo, setReplyTo] = React.useState<Identifiable<C> | null>(null);
  const { pushView } = useModal();

  const subject = discussionRow.subject.uuid;
  const type = toDiscussionType(discussionRow.entityType);
  const {
    discussion,
    isRequestPending: isDiscussionRequestPending,
    isRequestSuccess,
    isRequestFailure,
    currentPage,
    canLoadMore,
    retrieveDiscussion,
  } = useRetrieveDiscussion(subject);

  React.useEffect(() => {
    !isDiscussionRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveDiscussion({ subject, type, page: 0, size: COMMENTS_PAGE_SIZE });
  }, [isRequestSuccess, isRequestFailure, isDiscussionRequestPending]);

  const commentsListRef = React.useRef<HTMLUListElement>(null);
  const isLoadingOlderComments = React.useRef(false);
  const scrollHeightBeforeLoadingOlderComments = React.useRef(0);

  const { isRequestPending, postComment } = usePostComment(discussion);
  const allComments = [...discussion.comments].sort(sortBySubmissionDate);
  const comments = allComments.filter((c) => !c.replyTo);
  const repliesMap = allComments
    .filter((c) => !!c.replyTo)
    .reduce<Record<string, Identifiable<C>[]>>((acc, reply) => {
      const parentUuid = reply.replyTo!.uuid;
      return { ...acc, [parentUuid]: [...(acc[parentUuid] ?? []), reply] };
    }, {});

  const canLoadOlderComments = !isDiscussionRequestPending && canLoadMore;
  const loadOlderComments = (target: HTMLUListElement) => {
    if (
      canLoadOlderComments &&
      !isLoadingOlderComments.current &&
      target.scrollTop <= LOAD_OLDER_COMMENTS_SCROLL_THRESHOLD
    ) {
      isLoadingOlderComments.current = true;
      scrollHeightBeforeLoadingOlderComments.current = target.scrollHeight;
      retrieveDiscussion({
        subject,
        type,
        page: currentPage,
        size: COMMENTS_PAGE_SIZE,
      });
    }
  };

  React.useEffect(() => {
    if (!commentsListRef.current) return;

    if (isLoadingOlderComments.current) {
      commentsListRef.current.scrollTop +=
        commentsListRef.current.scrollHeight -
        scrollHeightBeforeLoadingOlderComments.current;
      isLoadingOlderComments.current = false;
    } else {
      commentsListRef.current.scrollTop = commentsListRef.current.scrollHeight;
    }
  }, [discussion.comments.length]);

  return (
    <div
      className={classNames(styles.discussion, { [styles.closed]: !isOpen })}
    >
      <div className={styles.header} onClick={() => setIsOpen(!isOpen)}>
        <div
          className={styles.row}
          onClick={(e) => {
            e.stopPropagation();
            pushView(
              discussionRow.entityType === DiscussionSubjectTypes.EVENT
                ? {
                    key: UseCases.retrieveEvent,
                    data: { event: discussionRow.subject },
                  }
                : discussionRow.entityType === DiscussionSubjectTypes.MILESTONE
                  ? {
                      key: UseCases.retrieveMilestone,
                      data: { milestone: discussionRow.subject },
                    }
                  : discussionRow.entityType === DiscussionSubjectTypes.LIST
                    ? {
                        key: UseCases.retrieveList,
                        data: { listId: discussionRow.subject.uuid },
                      }
                    : discussionRow.entityType ===
                        DiscussionSubjectTypes.PROJECT
                      ? {
                          key: UseCases.retrieveProject,
                          data: { project: discussionRow.subject },
                        }
                      : {
                          key: UseCases.retrieveTask,
                          data: { task: discussionRow.subject },
                        },
            );
          }}
        >
          <div className={styles.iconBox}>
            <ToggleArrow className={styles.icon} />
          </div>
          {discussionRow.entityType === DiscussionSubjectTypes.EVENT ? (
            <EventIcon className={styles.subjectIcon} />
          ) : discussionRow.entityType === DiscussionSubjectTypes.MILESTONE ? (
            <MilestoneIcon className={styles.subjectIcon} />
          ) : discussionRow.entityType === DiscussionSubjectTypes.LIST ? (
            <ListIcon className={styles.subjectIcon} />
          ) : discussionRow.entityType === DiscussionSubjectTypes.PROJECT ? (
            <ProjectIcon className={styles.subjectIcon} />
          ) : (
            <TaskIcon className={styles.subjectIcon} />
          )}
          <p className={styles.name}>{discussionRow.subject.name}</p>
        </div>
        <p className={styles.commentCount}>
          {discussionRow.commentsCount ?? discussion.comments.length}{" "}
          {t("projectPage.discussionsPage.comments")}
        </p>
        <div className={styles.contextualMenu}>
          <ContextualMenu
            items={[
              {
                name: t("projectPage.discussionsPage.remove"),
                renderIcon: (className: string) => (
                  <TrashIcon
                    className={classNames(className, styles.trashIcon)}
                  />
                ),
                color: "#db5f63",
                act: () =>
                  pushView({
                    data: discussionRow,
                    key: UseCases.removeDiscussion,
                  }),
              },
            ]}
          />
        </div>
      </div>
      <ul
        className={styles.comments}
        ref={commentsListRef}
        onScroll={(e) => loadOlderComments(e.currentTarget)}
      >
        {isOpen && isDiscussionRequestPending && currentPage > 0 && (
          <li>
            <CommentLoader />
          </li>
        )}
        {(isOpen
          ? comments
          : comments.slice(comments.length - 1, comments.length)
        ).map((comment) => (
          <React.Fragment key={comment.uuid}>
            <li>
              <Comment
                comment={comment}
                resources={resources}
                replies={repliesMap[comment.uuid] ?? []}
                onReply={(c) => setReplyTo(c)}
              />
            </li>
            {replyTo?.uuid === comment.uuid && (
              <li className={styles.replyInput}>
                <ChatInput
                  isSubmitting={isRequestPending}
                  replyTo={replyTo}
                  onCancelReply={() => setReplyTo(null)}
                  submit={(comment) => {
                    postComment({
                      account,
                      discussion,
                      comment: {
                        ...comment,
                        author: account.resource,
                      },
                    });
                    setReplyTo(null);
                  }}
                />
              </li>
            )}
          </React.Fragment>
        ))}
        {isOpen && (
          <li>
            <ChatInput
              isSubmitting={isRequestPending}
              submit={(comment) => {
                postComment({
                  account,
                  discussion,
                  comment: {
                    ...comment,
                    author: account.resource,
                  },
                });
              }}
            />
          </li>
        )}
      </ul>
    </div>
  );
}

export { Discussion };
